<template>
  <b-row>
    <b-col cols="12" class="mb-32 mt-16">
      <breadcrumb activeTitle="Integración APP Copec" />
    </b-col>

    <b-col cols="12" class="mb-32">
      <h2 class="mb-0">Integración APP Copec</h2>
      <h5 class="mb-0 text-black-80 hp-text-color-dark-30">
        Envío de los números de serie de los inversores a la APP Copec
      </h5>
    </b-col>

    <b-col cols="12" v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </b-col>

    <b-col cols="12" v-else-if="config">
      <b-card class="mb-24" title="Credenciales del servidor">
        <b-row>
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">URL base</small>
            <strong>{{ config.env.base_url || "sin configurar" }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">Tenant</small>
            <strong>{{
              config.env.tenant_id_set ? "configurado" : "sin configurar"
            }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">API key</small>
            <strong>{{ config.env.api_key_hint }}</strong>
          </b-col>
        </b-row>
        <b-alert
          v-if="config.env.missing.length"
          show
          variant="danger"
          class="mt-3 mb-0"
        >
          Faltan variables de entorno en el servidor:
          {{ config.env.missing.join(", ") }}. Sin ellas el envío no funciona.
        </b-alert>
        <p class="text-black-60 mt-3 mb-0">
          Estas credenciales se configuran mediante variables de entorno y no se
          editan desde el panel.
        </p>
      </b-card>

      <b-card class="mb-24" title="Plantilla y preguntas">
        <b-form-group label="Plantilla que dispara el envío">
          <b-form-select
            v-model="form.checklist"
            :options="opcionesPlantilla"
            @change="cambiarPlantilla"
          ></b-form-select>
        </b-form-group>

        <b-form-group
          v-for="campo in campos"
          :key="campo.clave"
          :label="campo.etiqueta"
        >
          <div class="d-flex align-items-center">
            <b-form-select
              v-model="form[campo.clave]"
              :options="opcionesPregunta(campo.tipo)"
              class="mr-2"
            ></b-form-select>
            <b-badge :variant="estadoPregunta(campo.bloque).variant">
              {{ estadoPregunta(campo.bloque).texto }}
            </b-badge>
          </div>
        </b-form-group>
      </b-card>

      <b-card class="mb-24" title="Comportamiento">
        <b-form-checkbox v-model="form.enabled" class="mb-2"
          >Integración activa</b-form-checkbox
        >
        <b-form-checkbox v-model="form.auto_send_on_full_approval" class="mb-2">
          Enviar automáticamente al aprobarse el 100 % de la plantilla
        </b-form-checkbox>
        <b-alert v-if="form.auto_send_on_full_approval" show variant="info">
          El envío automático todavía no está enganchado en el flujo de
          aprobación: esta casilla queda preparada para cuando se active.
        </b-alert>
        <b-form-checkbox v-model="form.require_approved_answers" class="mb-3">
          Enviar sólo respuestas aprobadas
        </b-form-checkbox>

        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Espera de conexión (s)">
              <b-form-input
                v-model.number="form.timeout_connect"
                type="number"
                min="1"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Espera de respuesta (s)">
              <b-form-input
                v-model.number="form.timeout_read"
                type="number"
                min="1"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Reintentos automáticos">
              <b-form-input
                v-model.number="form.max_retries"
                type="number"
                min="0"
                max="5"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <b-card class="mb-24">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Marcas de inversor</h5>
          <b-button
            variant="primary"
            class="btn-ghost"
            @click="$refs.modalMarca.openModal(null)"
            >Agregar marca</b-button
          >
        </div>
        <b-table
          :items="config.brand_mappings"
          :fields="camposMarcas"
          small
          responsive
          bordered
          class="mb-0"
        >
          <template #cell(option_name)="{ item }">
            {{ item.option_name || "sin enlazar" }}
          </template>
          <template #cell(enabled)="{ item }">
            <b-badge :variant="item.enabled ? 'primary' : 'secondary'">{{
              item.enabled ? "Sí" : "No"
            }}</b-badge>
          </template>
          <template #cell(acciones)="{ item }">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              @click="$refs.modalMarca.openModal(item)"
              >Editar</b-button
            >
            <b-button size="sm" variant="outline-danger" @click="borrarMarca(item)"
              >Eliminar</b-button
            >
          </template>
        </b-table>
        <p v-if="!config.brand_mappings.length" class="text-black-60 mt-3 mb-0">
          Todavía no hay ninguna marca mapeada. Sin mapeo, el envío falla para esa
          marca.
        </p>
      </b-card>

      <div class="d-flex justify-content-end mb-32">
        <b-button variant="primary" :disabled="guardando" @click="guardar">
          <b-spinner v-if="guardando" small type="grow" class="mr-1"></b-spinner>
          Guardar cambios
        </b-button>
      </div>
    </b-col>

    <modal-brand-mapping
      ref="modalMarca"
      :brandOptions="config ? config.brand_options : []"
      @guardar="guardarMarca"
    ></modal-brand-mapping>
  </b-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Breadcrumb from "@/layouts/components/content/breadcrumb/Breadcrumb.vue";
import { ROLES } from "@/constants/roles";
import ModalBrandMapping from "./ModalBrandMapping.vue";

export default {
  name: "IntegracionCopec",
  components: { Breadcrumb, ModalBrandMapping },
  data() {
    return {
      loading: true,
      guardando: false,
      plantillas: [],
      form: {},
      campos: [
        {
          clave: "question_location_code",
          bloque: "location_code",
          tipo: 10,
          etiqueta: "Pregunta del código de la APP Copec (Imagen única con texto)",
        },
        {
          clave: "question_serial",
          bloque: "serial",
          tipo: 10,
          etiqueta: "Pregunta de la etiqueta del inversor (Imagen única con texto)",
        },
        {
          clave: "question_brand",
          bloque: "brand",
          tipo: 9,
          etiqueta: "Pregunta de la marca del inversor (Listado)",
        },
      ],
      camposMarcas: [
        { key: "brand_name", label: "Marca" },
        { key: "option_name", label: "Opción de la pregunta" },
        { key: "device_provider_id", label: "ID de proveedor" },
        { key: "external_id_type", label: "Tipo de identificador" },
        { key: "enabled", label: "Activa", class: "text-center" },
        { key: "acciones", label: "Acciones", class: "text-center" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      config: "integrations/copecConfig",
      currentUser: "auth/currentUser",
    }),
    opcionesPlantilla() {
      return [{ value: null, text: "Sin seleccionar" }].concat(
        this.plantillas.map((p) => ({ value: p.id, text: p.name }))
      );
    },
  },
  async mounted() {
    if (!this.currentUser || this.currentUser.rol != ROLES.superadmin) {
      return this.$router.replace("/");
    }
    await this.cargar();
  },
  methods: {
    ...mapActions({
      getCopecConfig: "integrations/getCopecConfig",
      updateCopecConfig: "integrations/updateCopecConfig",
      saveCopecBrandMapping: "integrations/saveCopecBrandMapping",
      deleteCopecBrandMapping: "integrations/deleteCopecBrandMapping",
      getChecklists: "checklist/getChecklists",
    }),
    async cargar() {
      this.loading = true;
      // El listado de plantillas responde {items, pages, data:[...]}.
      const respuesta = await this.getChecklists({ page: 1, size: 500 });
      this.plantillas = Array.isArray(respuesta)
        ? respuesta
        : (respuesta && respuesta.data) || [];
      await this.getCopecConfig();
      this.rellenarFormulario();
      this.loading = false;
    },
    rellenarFormulario() {
      if (!this.config) return;
      this.form = {
        checklist: this.config.checklist ? this.config.checklist.id : null,
        question_location_code: this.config.questions.location_code.id,
        question_serial: this.config.questions.serial.id,
        question_brand: this.config.questions.brand.id,
        enabled: this.config.enabled,
        auto_send_on_full_approval: this.config.auto_send_on_full_approval,
        require_approved_answers: this.config.require_approved_answers,
        timeout_connect: this.config.timeout_connect,
        timeout_read: this.config.timeout_read,
        max_retries: this.config.max_retries,
      };
    },
    opcionesPregunta(tipo) {
      const todas = (this.config && this.config.question_choices) || [];
      const delTipo = todas.filter((q) => q.type_id === tipo);
      const resto = todas.filter((q) => q.type_id !== tipo);
      return [{ value: null, text: "Sin seleccionar" }]
        .concat(delTipo.map((q) => ({ value: q.id, text: q.name })))
        .concat(
          resto.map((q) => ({
            value: q.id,
            text: `${q.name} (otro tipo)`,
          }))
        );
    },
    estadoPregunta(bloque) {
      const datos = this.config && this.config.questions[bloque];
      if (!datos || !datos.id)
        return { variant: "secondary", texto: "Sin configurar" };
      if (datos.ok) return { variant: "primary", texto: "Verificado" };
      return { variant: "warning", texto: datos.problema };
    },
    async cambiarPlantilla() {
      // Al cambiar de plantilla las preguntas anteriores dejan de valer.
      this.form.question_location_code = null;
      this.form.question_serial = null;
      this.form.question_brand = null;
      await this.guardar(true);
    },
    async guardar(silencioso = false) {
      this.guardando = true;
      const respuesta = await this.updateCopecConfig(this.form);
      this.guardando = false;
      if (!respuesta.ok) {
        const detalle = Object.values(respuesta.data || {})[0];
        return this.$bvToast.toast(
          Array.isArray(detalle) ? detalle[0] : "No se pudo guardar la configuración",
          { title: "Error", variant: "danger", solid: false, noAutoHide: true }
        );
      }
      this.rellenarFormulario();
      if (silencioso !== true) {
        this.$bvToast.toast("Configuración de la integración APP Copec guardada", {
          title: "Excelente",
          variant: "primary",
          solid: false,
        });
      }
    },
    async guardarMarca(datos) {
      const respuesta = await this.saveCopecBrandMapping(datos);
      if (!respuesta.ok) {
        return this.$bvToast.toast("No se pudo guardar la marca", {
          title: "Error",
          variant: "danger",
          solid: false,
        });
      }
      await this.getCopecConfig();
      this.$bvToast.toast("Marca guardada", {
        title: "Excelente",
        variant: "primary",
        solid: false,
      });
    },
    async borrarMarca(item) {
      const confirmado = await this.$bvModal.msgBoxConfirm(
        "Los envíos de inversores de esta marca dejarán de funcionar.",
        {
          title: `¿Eliminar el mapeo de la marca «${item.brand_name}»?`,
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Eliminar",
          cancelTitle: "Cancelar",
          cancelVariant: "outline-info-3",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
        }
      );
      if (!confirmado) return;
      await this.deleteCopecBrandMapping(item.id);
      await this.getCopecConfig();
    },
  },
};
</script>
